import Grid from '@material-ui/core/Grid';
import Alert from '@material-ui/lab/Alert';
import MyCollapse from 'components/Collapse';
import React, { useState, useCallback } from 'react';
import { useObservable } from 'react-use-observable';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import type { Action, GroupModule } from 'v2/interfaces/access-control';

import Checkbox from './Checkbox';
import { ACCESS_POLICY_MODULES } from '../../constants';

type Props = {
  initialOpen: boolean;
  module: GroupModule;
  handleActionsChange: (actionId: number) => void;
  selectedActionsId: number[];
};

enum ACTIONS {
  VIEW = 'visualizar',
  EXPORT = 'exportar',
  SEE_CLIENT_DATA = 'ver dados clientes',
  HIDE_VALUES = 'ocultar valores',
}

export default function FormGroupModule({ initialOpen = false, module, handleActionsChange, selectedActionsId }: Props) {
  const [isOpen, setIsOpen] = useState(initialOpen);

  function handleCollapseToggle() {
    setIsOpen((prev) => !prev);
  }

  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const isBetaSubscriptionsDashboard = eduzzAccountsService.verifyUserHasBetaModule('vertical-assinaturas');
  const isBetaNewConnect = eduzzAccountsService.verifyUserHasBetaModule('new_connect');
  const isUnity = !!user?.is_clube_black;
  const hasBetaSubscriptionsDashboard = isBetaSubscriptionsDashboard || isUnity;

  const isProducer = user?.producer;
  const isModuleConnect = module.id === ACCESS_POLICY_MODULES.CONNECT;
  const showNewConnectAlert = isProducer && isBetaNewConnect && isModuleConnect;

  const filterActions = (action: Action) => {
    if (action.fullname === 'myeduzz:meus-contratos:dashboard' && !hasBetaSubscriptionsDashboard) return false;
    return true;
  };

  const getActionIdByFullName = useCallback(
    (fullName: string) => {
      const action = module.actions.find((action) => action.fullname === fullName);
      return action?.id;
    },
    [module.actions]
  );

  const blinketDisableAction = useCallback(
    (action: Action) => {
      const isBlinketEventsReadSelected = selectedActionsId.includes(getActionIdByFullName('myeduzz:blinket_events:visualizar'));
      const isBlinketEventsAdmSelected = selectedActionsId.includes(getActionIdByFullName('myeduzz:blinket_events:adm'));
      const isBlinketParticipantsReadSelected = selectedActionsId.includes(getActionIdByFullName('myeduzz:blinket_participants:visualizar'));
      const isBlinketParticipantsAdmSelected = selectedActionsId.includes(getActionIdByFullName('myeduzz:blinket_participants:adm'));

      if (action.fullname === 'myeduzz:blinket_events:adm' && !isBlinketEventsReadSelected) {
        return true;
      }
      if (action.fullname === 'myeduzz:blinket_participants:adm' && (!isBlinketParticipantsReadSelected || !isBlinketEventsReadSelected)) {
        return true;
      }
      if (
        action.fullname === 'myeduzz:blinket_events:visualizar' &&
        isBlinketEventsReadSelected &&
        (isBlinketEventsAdmSelected || isBlinketParticipantsAdmSelected || isBlinketParticipantsReadSelected)
      ) {
        return true;
      }
      if (action.fullname === 'myeduzz:blinket_participants:visualizar') {
        if (!isBlinketEventsReadSelected) {
          return true;
        }
        if (isBlinketParticipantsAdmSelected && isBlinketParticipantsReadSelected) {
          return true;
        }
      }

      return false;
    },
    [getActionIdByFullName, selectedActionsId]
  );
  const shouldDisableAction = useCallback(
    (action: Action) => {
      const moduleActions = module.actions;
      const loweredActionName = action.name.toLowerCase();
      const viewActionInModule = moduleActions.find((action) => action.name.toLowerCase().includes(ACTIONS.VIEW));
      const hasViewActionInModule = !!viewActionInModule;
      const hasNoActionsSelectedInModule = !moduleActions.some((action) => selectedActionsId.includes(action.id));
      const isViewAction = loweredActionName.includes(ACTIONS.VIEW);
      const isExportAction = loweredActionName === ACTIONS.EXPORT;
      const isSeeClientDataAction = loweredActionName === ACTIONS.SEE_CLIENT_DATA;
      const hasHideValuesActionInModule = loweredActionName === ACTIONS.HIDE_VALUES;
      const isNotNutrorAdmAction = action.fullname !== 'myeduzz:nutror:adm';
      const isBlinket = action.fullname.includes('blinket');

      if (isBlinket) {
        return blinketDisableAction(action);
      }

      if (hasViewActionInModule && isNotNutrorAdmAction) {
        if (hasNoActionsSelectedInModule) {
          return !isViewAction;
        }
        if (isExportAction) {
          const seeClientDataAction = moduleActions.find((action) => action.name.toLowerCase().includes(ACTIONS.SEE_CLIENT_DATA));
          const hasSeeClientDataActionInModule = !!seeClientDataAction?.status;
          const hideValuesActionInModule = moduleActions.find((action) => action.name.toLowerCase().includes(ACTIONS.HIDE_VALUES));
          const hasHideValuesActionInModule = !!hideValuesActionInModule;
          if (hasHideValuesActionInModule && hasSeeClientDataActionInModule) {
            const isSeeClientDataActionSelected = selectedActionsId.includes(seeClientDataAction.id);
            const isHideValuesActionInModule = selectedActionsId.includes(hideValuesActionInModule.id);
            return !isSeeClientDataActionSelected || isHideValuesActionInModule;
          }
        }
        if (isSeeClientDataAction || hasHideValuesActionInModule) {
          const exportAction = moduleActions.find((action) => action.name.toLowerCase().includes(ACTIONS.EXPORT));
          const hasExportAction = !!exportAction;
          if (hasExportAction) {
            const isExportActionSelected = selectedActionsId.includes(exportAction.id);
            return isExportActionSelected;
          }
        }

        const isViewActionSelected = selectedActionsId.includes(viewActionInModule.id);
        const selectedModuleActions = moduleActions.filter((action) => selectedActionsId.includes(action.id));
        const notOnlyViewActionIsSelected = !(selectedModuleActions.length === 1 && selectedModuleActions[0].id === viewActionInModule.id);
        return isViewAction && isViewActionSelected && notOnlyViewActionIsSelected;
      }
      return false;
    },
    [selectedActionsId, module, blinketDisableAction]
  );

  const onActionsChange = useCallback(
    (action: Action, checked: boolean) => {
      const loweredActionName = action.name.toLowerCase();
      if (loweredActionName === ACTIONS.VIEW && checked) {
        const seeClientDataAction = module.actions.find((action) => action.name.toLowerCase().includes(ACTIONS.SEE_CLIENT_DATA));
        if (seeClientDataAction) handleActionsChange(seeClientDataAction.id);
      }
      handleActionsChange(action.id);
    },
    [handleActionsChange, module.actions]
  );

  return (
    <>
      <div className='m8'></div>

      <MyCollapse title={module.name} arrow contentActive={isOpen} onClick={handleCollapseToggle}>
        <Grid container wrap='wrap' spacing={2}>
          {module.actions.filter(filterActions).map((action) => (
            <>
              <Checkbox
                key={action.id}
                module={module}
                isChecked={selectedActionsId.includes(action.id)}
                isDisabled={shouldDisableAction(action)}
                action={action}
                handleActionsChange={onActionsChange}
              />
              {action.fullname === 'myeduzz:analytics:visualizar' && (
                <Alert severity='warning' style={{ width: '100%' }}>
                  Todos os dados do Analytics serão exibidos para o usuário ao liberar o acesso visualizar.
                </Alert>
              )}
            </>
          ))}
        </Grid>
        {showNewConnectAlert && (
          <Grid container wrap='wrap' spacing={2}>
            <Alert severity='warning' style={{ width: '100%' }}>
              O controle de acesso funcionará apenas para a versão anterior do connect, estamos trabalhando para implementar essa funcionalidade para
              nova versão em breve.
            </Alert>
          </Grid>
        )}
      </MyCollapse>
    </>
  );
}
